import React, { useState, useEffect } from 'react';
import {
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  Badge,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  DarkModeOutlined,
  LightModeOutlined,
  NotificationsNoneOutlined,
  LogoutOutlined,
  PersonOutline,
  FiberManualRecord,
  TranslateOutlined,
  Check,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useAppTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';

const AppBar = ({ handleDrawerToggle, toggleCollapse, isCollapsed }) => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useAppTheme();
  const { language, setLanguage, isRtl, dir, t } = useLanguage();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLangMenuOpen = (event) => {
    setLangAnchorEl(event.currentTarget);
  };

  const handleLangMenuClose = () => {
    setLangAnchorEl(null);
  };

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    handleLangMenuClose();
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/');
  };

  const formattedTime = time.toLocaleTimeString(language === 'ar' ? 'ar-EG' : 'en-US', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <MuiAppBar
      position="sticky"
      elevation={0}
      dir={dir}
      style={{ direction: dir }}
      sx={{
        bgcolor: 'background.paper',
        color: 'text.primary',
        borderBottom: '1px solid',
        borderColor: 'divider',
        height: 64,
        justifyContent: 'center',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        dir={dir}
        style={{ direction: dir }}
        sx={{
          justifyContent: 'space-between',
          px: { xs: 2, sm: 2.5 },
          direction: dir,
        }}
      >
        {/* Leading side: Sidebar Toggle & Branch status */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* Mobile Hamburger Toggle */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            size="small"
            sx={{ display: { lg: 'none' } }}
          >
            <MenuIcon fontSize="small" />
          </IconButton>

          {/* Desktop Sidebar Collapse Toggle */}
          <IconButton
            color="inherit"
            onClick={toggleCollapse}
            size="small"
            sx={{ display: { xs: 'none', lg: 'inline-flex' } }}
          >
            {isCollapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
          </IconButton>

          {/* Active Branch Status */}
          <Chip
            icon={<FiberManualRecord sx={{ fontSize: '10px !important', color: '#10B981 !important' }} />}
            label={isRtl ? 'فرع القاهرة الرئيسي' : 'Cairo Main Branch'}
            size="small"
            sx={{
              display: { xs: 'none', sm: 'inline-flex' },
              fontWeight: 600,
              fontSize: '0.75rem',
              bgcolor: 'action.hover',
              border: '1px solid',
              borderColor: 'divider',
            }}
          />
        </Box>

        {/* Trailing side: Time, Language, Theme, Notifications, Profile */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Clock */}
          <Typography
            variant="caption"
            sx={{
              display: { xs: 'none', md: 'block' },
              fontWeight: 700,
              color: 'text.secondary',
              px: 1,
              py: 0.4,
              borderRadius: '6px',
              bgcolor: 'action.hover',
            }}
          >
            {formattedTime}
          </Typography>

          {/* Language Switcher */}
          <Tooltip title={t('language')}>
            <Button
              onClick={handleLangMenuOpen}
              color="inherit"
              size="small"
              startIcon={<TranslateOutlined sx={{ fontSize: 18, color: 'primary.main' }} />}
              sx={{
                minWidth: 'auto',
                px: 1,
                py: 0.5,
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '0.78rem',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              {language === 'ar' ? 'العربية' : 'EN'}
            </Button>
          </Tooltip>

          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={handleLangMenuClose}
            transformOrigin={{ horizontal: isRtl ? 'right' : 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: isRtl ? 'right' : 'left', vertical: 'bottom' }}
            PaperProps={{
              elevation: 4,
              sx: {
                minWidth: 150,
                borderRadius: '10px',
                mt: 0.8,
                direction: dir,
              },
            }}
          >
            <MenuItem
              selected={language === 'ar'}
              onClick={() => handleLanguageChange('ar')}
              sx={{ fontSize: '0.82rem', fontWeight: language === 'ar' ? 700 : 400 }}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                {language === 'ar' ? <Check fontSize="small" color="primary" /> : null}
              </ListItemIcon>
              <ListItemText primary="🇸🇦 العربية (العربية)" />
            </MenuItem>
            <MenuItem
              selected={language === 'en'}
              onClick={() => handleLanguageChange('en')}
              sx={{ fontSize: '0.82rem', fontWeight: language === 'en' ? 700 : 400 }}
            >
              <ListItemIcon sx={{ minWidth: 30 }}>
                {language === 'en' ? <Check fontSize="small" color="primary" /> : null}
              </ListItemIcon>
              <ListItemText primary="🇺🇸 English (US)" />
            </MenuItem>
          </Menu>

          {/* Theme Toggle */}
          <Tooltip title={isDarkMode ? t('lightMode') : t('darkMode')}>
            <IconButton onClick={toggleTheme} color="inherit" size="small">
              {isDarkMode ? <LightModeOutlined sx={{ fontSize: 19, color: '#F59E0B' }} /> : <DarkModeOutlined sx={{ fontSize: 19 }} />}
            </IconButton>
          </Tooltip>

          {/* Notifications */}
          <Tooltip title={isRtl ? 'التنبيهات' : 'Notifications'}>
            <IconButton color="inherit" size="small">
              <Badge badgeContent={3} color="primary" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem', height: 16, minWidth: 16 } }}>
                <NotificationsNoneOutlined sx={{ fontSize: 20 }} />
              </Badge>
            </IconButton>
          </Tooltip>

          <Divider orientation="vertical" flexItem sx={{ height: 20, my: 'auto', mx: 0.5 }} />

          {/* User Profile */}
          <Box
            onClick={handleProfileMenuOpen}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              p: 0.4,
              borderRadius: '8px',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontWeight: 700,
                fontSize: '0.82rem',
              }}
            >
              {user?.userName ? user.userName.charAt(0).toUpperCase() : 'A'}
            </Avatar>

            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: isRtl ? 'right' : 'left' }}>
              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2 }}>
                {user?.displayName || user?.userName || (isRtl ? 'مدير النظام' : 'System Admin')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                {user?.role || 'CompanyAdmin'}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            transformOrigin={{ horizontal: isRtl ? 'right' : 'left', vertical: 'top' }}
            anchorOrigin={{ horizontal: isRtl ? 'right' : 'left', vertical: 'bottom' }}
            PaperProps={{
              elevation: 4,
              sx: {
                minWidth: 190,
                borderRadius: '10px',
                mt: 0.8,
                direction: dir,
              },
            }}
          >
            <MenuItem onClick={() => { handleMenuClose(); navigate('/company'); }} sx={{ fontSize: '0.82rem' }}>
              <ListItemIcon>
                <PersonOutline fontSize="small" />
              </ListItemIcon>
              {t('nav.companyProfile')}
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem onClick={handleLogout} sx={{ color: 'error.main', fontSize: '0.82rem' }}>
              <ListItemIcon sx={{ color: 'error.main' }}>
                <LogoutOutlined fontSize="small" />
              </ListItemIcon>
              {t('logout')}
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </MuiAppBar>
  );
};

export default AppBar;
